import type { DictFragment } from '../translations';

/** Session recorder: the top-bar tab and the deck it slides out (components/layout/SessionRecorder.tsx). */
export const recorder: DictFragment = {
  fr: {
    'recorder.record': 'Enregistrer',
    'recorder.deck': 'Enregistreur de séance',
    'recorder.openDeck': 'Ouvrir l’enregistreur',
    'recorder.close': 'Fermer l’enregistreur',
    'recorder.ready': 'Prêt',
    'recorder.recordSession': 'Enregistrer une séance',
    'recorder.recordMeeting': 'Enregistrer cette séance',
    'recorder.inMeeting': 'Séance en cours',
    'recorder.meetingTime': '{from}–{to}',
    'recorder.sourceLabel': 'Son enregistré',
    'recorder.sourceRoom': 'Dans la salle',
    'recorder.sourceOnline': '+ en ligne',
    'recorder.sourceOnlineHelp': 'Enregistre aussi le son de la réunion en ligne : le navigateur vous demandera de partager l’onglet ou l’écran de la réunion, avec son audio.',
    'recorder.pickProject': 'Pour quel projet ?',
    'recorder.pickPlaceholder': 'Choisir un projet…',
    'recorder.loading': 'Chargement…',
    'recorder.otherProject': 'Autre projet',
    'recorder.cancel': 'Annuler',
    'recorder.starting': 'Démarrage…',
    'recorder.recording': 'Enregistrement',
    'recorder.paused': 'En pause',
    'recorder.sending': 'Envoi',
    'recorder.sendingHelp': 'Envoi des dernières secondes…',
    'recorder.pause': 'Pause',
    'recorder.resume': 'Reprendre',
    'recorder.stop': 'Terminer',
    'recorder.discard': 'Supprimer l’enregistrement',
    'recorder.discardShort': 'Supprimer',
    'recorder.discardConfirm': 'Supprimer cet enregistrement ? Il ne sera pas envoyé à Speakr.',
    'recorder.onlineLost': 'Le partage de la réunion en ligne s’est arrêté : seul le micro est enregistré.',
    'recorder.noSound': 'Aucun son capté depuis quelques secondes — vérifiez le micro.',
    'recorder.noSoundAtAll': '« {mic} » ne capte rien. Essayez un autre micro, l’enregistrement repart dessus.',
    'recorder.mic': 'Micro',
    'recorder.micDefault': 'Micro par défaut',
    'recorder.micSwitch': 'Recommencer avec…',
    'recorder.macMicHint': 'Sur Mac : Réglages Système › Confidentialité › Micro.',
    'recorder.micUnknown': 'inconnu',
    'recorder.offline': 'Connexion interrompue, {n} morceau(x) en attente : ne fermez pas l’onglet.',
    'recorder.orphan': 'Enregistrement interrompu',
    'recorder.orphanHelp': 'environ {n} min reçues avant la fermeture de l’onglet',
    'recorder.orphanFinish': 'Envoyer à Speakr',
    'recorder.notConnected': 'Votre compte Speakr n’est pas encore relié à PMO.',
    'recorder.connect': 'Relier dans le profil',
    'recorder.dismiss': 'OK',
    'recorder.open': 'Ouvrir',
    'recorder.retry': 'Réessayer',
    'recorder.status.uploading': 'Envoi à Speakr…',
    'recorder.status.processing': 'Transcription en cours {code} : elle arrivera dans la Chronologie',
    'recorder.status.completed': 'Transcription prête',
    'recorder.status.failed': 'L’envoi à Speakr a échoué',
    'recorder.error.mic_denied': 'Le micro est bloqué : autorisez-le dans le navigateur.',
    'recorder.error.online_cancelled': 'Partage de la réunion annulé.',
    'recorder.error.online_no_audio': 'Aucun son partagé : cochez « Partager l’audio » en choisissant l’onglet ou l’écran.',
    'recorder.error.no_speakr': 'Votre compte Speakr n’est pas relié à PMO.',
    'recorder.error.start_failed': 'L’enregistrement n’a pas pu démarrer.',
    'recorder.error.no_mic': 'Aucun micro trouvé sur cet ordinateur.',
    'recorder.error.mic_busy': 'Le micro est utilisé par une autre application ou bloqué par le système (Réglages › Confidentialité › Micro).',
    'recorder.error.stop_failed': 'L’enregistrement est conservé, mais l’envoi n’a pas pu démarrer : réessayez.',
    'recorder.error.closed': 'Cet enregistrement a été terminé ailleurs.',
    'timeline.transcribing': 'Transcription en cours…',
    'timeline.recordedInPmo': 'Enregistré dans PMO',
    'audioImport.title': 'Enregistrement audio',
    'audioImport.reading': 'Lecture…',
    'audioImport.lengthUnknown': 'durée inconnue',
    'audioImport.question': 'Qu’est-ce que c’est ?',
    'audioImport.kindMeeting': 'Une séance',
    'audioImport.kindMeetingHint': 'Va dans la Chronologie du projet, avec sa transcription.',
    'audioImport.kindDocument': 'Un document audio',
    'audioImport.kindDocumentHint': 'Reste ici dans les documents, avec sa transcription en texte.',
    'audioImport.kindDocumentHintAt': 'Reste dans les documents ({target}), avec sa transcription en texte.',
    'audioImport.date': 'Date',
    'audioImport.time': 'Début',
    'audioImport.fromName': 'Date et heure lues dans le nom du fichier.',
    'audioImport.fromFile': 'Date et heure déduites du fichier : vérifiez-les.',
    'audioImport.meeting': 'Séance Outlook',
    'audioImport.looking': 'Recherche…',
    'audioImport.duringIt': 'à cette heure',
    'audioImport.meetingNone': 'Aucune : une entrée à part dans la Chronologie',
    'audioImport.joinsMeeting': 'La transcription s’ajoute à « {subject} », à côté de celles qu’elle a déjà.',
    'audioImport.noMeetingThatDay': 'Aucune séance du projet ce jour-là : l’enregistrement aura sa propre entrée.',
    'audioImport.standalone': 'L’enregistrement aura sa propre entrée dans la Chronologie.',
    'audioImport.subject': 'Sujet',
    'audioImport.notConnected': 'La transcription passe par votre compte Speakr, qui n’est pas relié à PMO (Profil › Speakr). Vous pouvez joindre le fichier sans le transcrire.',
    'audioImport.failed': 'L’envoi a échoué : {error}',
    'audioImport.sending': 'Envoi… {pct} %',
    'audioImport.saving': 'Enregistrement…',
    'audioImport.attachOnly': 'Joindre sans transcrire',
    'audioImport.transcribe': 'Transcrire',
    'timeline.importAudio': 'Audio',
    'timeline.importAudioTitle': 'Importer l’enregistrement d’une séance (téléphone, dictaphone…) pour le transcrire',
    'timeline.fromSpeakr': 'Speakr',
    'timeline.fromRecorder': 'PMO',
    'timeline.fromUpload': 'audio importé',
    'timeline.transcriptFrom': 'Transcription · {source}',
    'timeline.transcriptFailed': 'Transcription échouée',
    'docs.transcript': 'Transcription',
  },
  en: {
    'recorder.record': 'Record',
    'recorder.deck': 'Session recorder',
    'recorder.openDeck': 'Open the recorder',
    'recorder.close': 'Close the recorder',
    'recorder.ready': 'Ready',
    'recorder.recordSession': 'Record a session',
    'recorder.recordMeeting': 'Record this meeting',
    'recorder.inMeeting': 'Meeting now',
    'recorder.meetingTime': '{from}–{to}',
    'recorder.sourceLabel': 'Recorded sound',
    'recorder.sourceRoom': 'In the room',
    'recorder.sourceOnline': '+ online',
    'recorder.sourceOnlineHelp': 'Also records the online meeting’s sound: the browser will ask you to share the meeting’s tab or screen, with its audio.',
    'recorder.pickProject': 'Which project?',
    'recorder.pickPlaceholder': 'Choose a project…',
    'recorder.loading': 'Loading…',
    'recorder.otherProject': 'Other project',
    'recorder.cancel': 'Cancel',
    'recorder.starting': 'Starting…',
    'recorder.recording': 'Recording',
    'recorder.paused': 'Paused',
    'recorder.sending': 'Sending',
    'recorder.sendingHelp': 'Sending the last seconds…',
    'recorder.pause': 'Pause',
    'recorder.resume': 'Resume',
    'recorder.stop': 'Finish',
    'recorder.discard': 'Delete the recording',
    'recorder.discardShort': 'Delete',
    'recorder.discardConfirm': 'Delete this recording? It will not be sent to Speakr.',
    'recorder.onlineLost': 'Sharing of the online meeting stopped: only the microphone is recorded.',
    'recorder.noSound': 'No sound picked up for a few seconds — check the microphone.',
    'recorder.noSoundAtAll': '“{mic}” picks up nothing. Try another microphone; the recording restarts on it.',
    'recorder.mic': 'Microphone',
    'recorder.micDefault': 'Default microphone',
    'recorder.micSwitch': 'Restart with…',
    'recorder.macMicHint': 'On a Mac: System Settings › Privacy › Microphone.',
    'recorder.micUnknown': 'unknown',
    'recorder.offline': 'Connection lost, {n} piece(s) waiting: do not close the tab.',
    'recorder.orphan': 'Recording interrupted',
    'recorder.orphanHelp': 'about {n} min received before the tab closed',
    'recorder.orphanFinish': 'Send to Speakr',
    'recorder.notConnected': 'Your Speakr account is not linked to PMO yet.',
    'recorder.connect': 'Link it in your profile',
    'recorder.dismiss': 'OK',
    'recorder.open': 'Open',
    'recorder.retry': 'Retry',
    'recorder.status.uploading': 'Sending to Speakr…',
    'recorder.status.processing': 'Transcribing {code}, it will appear in the Timeline',
    'recorder.status.completed': 'Transcript ready',
    'recorder.status.failed': 'Sending to Speakr failed',
    'recorder.error.mic_denied': 'The microphone is blocked: allow it in the browser.',
    'recorder.error.online_cancelled': 'Meeting sharing cancelled.',
    'recorder.error.online_no_audio': 'No sound shared: tick “Share audio” when choosing the tab or screen.',
    'recorder.error.no_speakr': 'Your Speakr account is not linked to PMO.',
    'recorder.error.start_failed': 'The recording could not start.',
    'recorder.error.no_mic': 'No microphone found on this computer.',
    'recorder.error.mic_busy': 'The microphone is used by another app or blocked by the system (Settings › Privacy › Microphone).',
    'recorder.error.stop_failed': 'The recording is kept, but sending could not start: try again.',
    'recorder.error.closed': 'This recording was finished elsewhere.',
    'timeline.transcribing': 'Transcribing…',
    'timeline.recordedInPmo': 'Recorded in PMO',
    'audioImport.title': 'Audio recording',
    'audioImport.reading': 'Reading…',
    'audioImport.lengthUnknown': 'length unknown',
    'audioImport.question': 'What is it?',
    'audioImport.kindMeeting': 'A meeting',
    'audioImport.kindMeetingHint': 'Goes into the project’s timeline, with its transcript.',
    'audioImport.kindDocument': 'An audio document',
    'audioImport.kindDocumentHint': 'Stays here in the documents, with its transcript as text.',
    'audioImport.kindDocumentHintAt': 'Stays in the documents ({target}), with its transcript as text.',
    'audioImport.date': 'Date',
    'audioImport.time': 'Start',
    'audioImport.fromName': 'Date and time read from the file name.',
    'audioImport.fromFile': 'Date and time worked out from the file: check them.',
    'audioImport.meeting': 'Outlook meeting',
    'audioImport.looking': 'Looking…',
    'audioImport.duringIt': 'at that time',
    'audioImport.meetingNone': 'None: an entry of its own in the timeline',
    'audioImport.joinsMeeting': 'The transcript is added to “{subject}”, next to the ones it already has.',
    'audioImport.noMeetingThatDay': 'No meeting of the project that day: the recording gets an entry of its own.',
    'audioImport.standalone': 'The recording gets an entry of its own in the timeline.',
    'audioImport.subject': 'Subject',
    'audioImport.notConnected': 'Transcription goes through your Speakr account, which is not linked to PMO (Profile › Speakr). You can attach the file without transcribing it.',
    'audioImport.failed': 'Sending failed: {error}',
    'audioImport.sending': 'Sending… {pct} %',
    'audioImport.saving': 'Saving…',
    'audioImport.attachOnly': 'Attach without transcribing',
    'audioImport.transcribe': 'Transcribe',
    'timeline.importAudio': 'Audio',
    'timeline.importAudioTitle': 'Import a meeting recording (phone, voice recorder…) to transcribe it',
    'timeline.fromSpeakr': 'Speakr',
    'timeline.fromRecorder': 'PMO',
    'timeline.fromUpload': 'imported audio',
    'timeline.transcriptFrom': 'Transcript · {source}',
    'timeline.transcriptFailed': 'Transcription failed',
    'docs.transcript': 'Transcript',
  },
};
